import { motion } from "framer-motion";

const PILLARS = [
  {
    label: "Twelve Years",
    title: "of dedicated chauffeur experience",
    body: "Career drivers with deep Sydney knowledge — from harbour hotels to regional NSW.",
  },
  {
    label: "Fifteen Minutes",
    title: "ahead of every scheduled pickup",
    body: "A punctuality standard we maintain regardless of traffic or terminal.",
  },
  {
    label: "Absolute Discretion",
    title: "for confidential travel",
    body: "Suitable for confidential meetings, private guests and sensitive itineraries.",
  },
  {
    label: "Daily Detailing",
    title: "and rigorous mechanical checks",
    body: "Every vehicle presented immaculately, inside and out, before each journey.",
  },
  {
    label: "Transparent Fares",
    title: "with no surge or surprise",
    body: "Clear all-inclusive pricing communicated at the point of booking.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function WhySydneyJames() {
  return (
    <section
      data-testid="why-sydney-james-section"
      className="bg-stone text-charcoal py-24 md:py-36 border-t border-charcoal/10"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-24">
        {/* Left column — sticky editorial heading */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <motion.p
              {...fadeUp}
              className="sjc-eyebrow text-muted-light flex items-center gap-4"
            >
              <span className="sjc-hairline w-8" />
              Why Sydney James
            </motion.p>
            <motion.h2
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 }}
              className="font-display mt-6 text-4xl md:text-5xl lg:text-[60px] leading-[1.05] tracking-[-0.015em]"
            >
              Quiet authority,
              <br />
              <span className="italic font-light"> uncompromising standards.</span>
            </motion.h2>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.16 }}
              className="mt-8 text-muted-light text-base md:text-lg leading-[1.75] font-light max-w-md"
            >
              Every element of the service is designed so that the moments of your day
              you actually care about — the meeting, the flight, the reservation — remain
              undisturbed.
            </motion.p>
          </div>
        </div>

        {/* Right column — restrained pillar list */}
        <div className="lg:col-span-7 space-y-14 md:space-y-16">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`pillar-${i}`}
              className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 pb-14 md:pb-16 border-b border-charcoal/10 last:border-b-0 last:pb-0"
            >
              <div className="pt-1.5">
                <span className="font-display text-gold/80 text-2xl italic">
                  0{i + 1}
                </span>
              </div>
              <div>
                <p className="sjc-eyebrow text-muted-light">
                  {p.label}
                </p>
                <h3 className="font-display mt-3 text-3xl md:text-[36px] leading-[1.15] tracking-tight text-charcoal">
                  {p.title}
                </h3>
                <p className="mt-5 text-muted-light text-[15px] md:text-base leading-[1.8] font-light max-w-xl">
                  {p.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
